import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Calendar, Clock, List, Table2, UserCircle } from 'lucide-react';
import { format, isPast } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { Header } from '../components/Layout/Header';
import { Badge, getPriorityVariant, priorityLabels, statusLabels } from '../components/common/Badge';
import { Modal } from '../components/common/Modal';
import { TaskDetailPanel } from '../components/Tasks/TaskDetailPanel';
import { tasksApi, usersApi } from '../services/api';
import { useTaskStore } from '../store/taskStore';
import { Task, User } from '../types';
import { taskStatusColors } from '../constants/taskStatus';
import { TaskPeople } from '../components/Tasks/TaskPeople';
import { KanbanBoard } from '../components/Kanban/KanbanBoard';
import { TaskTableView } from '../components/Tasks/TaskTableView';

type ViewMode = 'kanban' | 'list' | 'table';

const TASKS_VIEW_STORAGE_KEY = 'crm-agro-tasks-page-view';

function getInitialViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'kanban';

  const storedView = window.localStorage.getItem(TASKS_VIEW_STORAGE_KEY);
  return storedView === 'list' || storedView === 'table' || storedView === 'kanban'
    ? storedView
    : 'kanban';
}

export function TasksPage() {
  const [searchParams] = useSearchParams();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [projectMembers, setProjectMembers] = useState<User[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>(getInitialViewMode);
  const { updateTask } = useTaskStore();

  const status = searchParams.get('status') || undefined;
  const assigneeId = searchParams.get('assigneeId') || undefined;
  const assigneeName = searchParams.get('assigneeName') || undefined;
  const workspaceId = searchParams.get('workspaceId') || undefined;

  const title = useMemo(() => {
    if (status) return `Tarefas: ${statusLabels[status] || status}`;
    if (assigneeName) return `Tarefas de ${assigneeName}`;
    return 'Tarefas';
  }, [assigneeName, status]);

  const loadTasks = useCallback(() => {
    setLoading(true);
    tasksApi.getAll({ status, assigneeId, workspaceId })
      .then(res => setTasks(res.data))
      .catch(() => toast.error('Falha ao carregar tarefas'))
      .finally(() => setLoading(false));
  }, [assigneeId, status, workspaceId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    usersApi.getAll()
      .then(res => setProjectMembers(res.data))
      .catch(() => setProjectMembers([]));
  }, []);

  useEffect(() => {
    window.localStorage.setItem(TASKS_VIEW_STORAGE_KEY, viewMode);
  }, [viewMode]);

  const handleUpdateTask = async (data: Partial<Task>) => {
    if (!selectedTask) return;
    try {
      await updateTask(selectedTask.id, data);
      setSelectedTask(null);
      loadTasks();
      toast.success('Tarefa atualizada!');
    } catch {
      toast.error('Falha ao atualizar tarefa');
    }
  };

  const handleMoveTask = async (taskId: string, newStatus: Task['status'], position: number) => {
    const previousTasks = tasks;
    const movedTask = previousTasks.find(task => task.id === taskId);
    if (!movedTask) return;

    const sourceTasks = previousTasks
      .filter(task => task.status === movedTask.status && task.id !== taskId)
      .sort((a, b) => a.position - b.position);
    const targetTasks = (movedTask.status === newStatus
      ? sourceTasks
      : previousTasks.filter(task => task.status === newStatus && task.id !== taskId)
    ).sort((a, b) => a.position - b.position);
    const safePosition = Math.max(0, Math.min(position, targetTasks.length));

    targetTasks.splice(safePosition, 0, { ...movedTask, status: newStatus, position: safePosition });

    const reordered = new Map<string, Task>();
    if (movedTask.status !== newStatus) {
      sourceTasks.forEach((task, index) => reordered.set(task.id, { ...task, position: index }));
    }
    targetTasks.forEach((task, index) => reordered.set(task.id, { ...task, status: newStatus, position: index }));

    setTasks(previousTasks.map(task => reordered.get(task.id) || task));

    try {
      await tasksApi.updatePosition(taskId, newStatus, safePosition);
      loadTasks();
    } catch {
      setTasks(previousTasks);
      toast.error('Falha ao mover tarefa');
    }
  };

  return (
    <div>
      <Header title={title} />
      <div className="p-6">
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-[#676879]">
          {status && (
            <span className="inline-flex items-center gap-2 rounded bg-white px-3 py-1.5 ring-1 ring-[#D0D4E4]">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: taskStatusColors[status as Task['status']] }} />
              Status: {statusLabels[status] || status}
            </span>
          )}
          {assigneeName && (
            <span className="inline-flex items-center gap-2 rounded bg-white px-3 py-1.5 ring-1 ring-[#D0D4E4]">
              <UserCircle size={15} />
              Atendente: {assigneeName}
            </span>
          )}
        </div>

        <div className="mb-6 flex items-center justify-between gap-3">
          <span className="text-sm text-[#676879]">{tasks.length} tarefas</span>
          <div className="flex overflow-hidden rounded-lg border border-gray-200 bg-white">
            <button
              type="button"
              onClick={() => setViewMode('kanban')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${
                viewMode === 'kanban' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Kanban"
            >
              Kanban
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${
                viewMode === 'list' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Lista"
            >
              <List size={14} />
              Lista
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${
                viewMode === 'table' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Tabela"
            >
              <Table2 size={14} />
              Tabela
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#C24914] border-t-transparent" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="py-16 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white ring-1 ring-[#D0D4E4]">
              <Clock size={24} className="text-[#C24914]" />
            </div>
            <h3 className="text-lg font-semibold text-[#323338]">Nenhuma tarefa encontrada</h3>
            <p className="mt-1 text-sm text-[#676879]">O filtro selecionado não retornou tarefas.</p>
          </div>
        ) : viewMode === 'kanban' ? (
          <KanbanBoard
            tasks={tasks}
            onMoveTask={handleMoveTask}
            onTaskClick={setSelectedTask}
            onAddTask={() => {}}
          />
        ) : viewMode === 'table' ? (
          <TaskTableView
            tasks={tasks}
            onTaskClick={setSelectedTask}
            onAddTask={() => {}}
            onStatusChange={async (taskId, status) => {
              try {
                await updateTask(taskId, { status });
                loadTasks();
                toast.success('Status atualizado!');
              } catch {
                toast.error('Falha ao atualizar status');
              }
            }}
          />
        ) : (
          <div className="space-y-2">
            {tasks.map(task => {
              const isOverdue = !!task.due_date && isPast(new Date(task.due_date)) && task.status !== 'done';
              return (
                <button
                  key={task.id}
                  type="button"
                  onClick={() => setSelectedTask(task)}
                  className="flex w-full items-center gap-4 rounded border border-[#D0D4E4] bg-white p-4 text-left transition hover:border-[#C24914] hover:shadow-sm"
                >
                  <span className="h-10 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: taskStatusColors[task.status] }} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[#323338]">{task.title}</p>
                    <p className="mt-0.5 truncate text-xs text-[#676879]">{task.project_name}</p>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                    <span
                      className="rounded px-2.5 py-1 text-xs font-semibold text-white"
                      style={{ backgroundColor: taskStatusColors[task.status] }}
                    >
                      {statusLabels[task.status] || task.status}
                    </span>
                    <Badge variant={getPriorityVariant(task.priority)}>{priorityLabels[task.priority] || task.priority}</Badge>
                    <TaskPeople task={task} showNames />
                    {task.due_date && (
                      <span className={`inline-flex items-center gap-1 text-xs ${isOverdue ? 'font-semibold text-[#E2445C]' : 'text-[#676879]'}`}>
                        {isOverdue ? <AlertCircle size={13} /> : <Calendar size={13} />}
                        {format(new Date(task.due_date), "d 'de' MMM", { locale: ptBR })}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <Modal isOpen={!!selectedTask} onClose={() => setSelectedTask(null)} title="" size="wide">
        {selectedTask && (
          <TaskDetailPanel
            projectId={selectedTask.project_id}
            projectName={selectedTask.project_name}
            initialData={selectedTask}
            members={projectMembers}
            onSubmit={handleUpdateTask}
            onCancel={() => setSelectedTask(null)}
          />
        )}
      </Modal>
    </div>
  );
}
